/**
 * CallerID Reputation Monitor - Main Application
 *
 * This is the root component that sets up:
 * - React Router for navigation
 * - Toast notifications provider
 * - Authentication state management
 * - Route protection
 */

import { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { Analytics } from '@vercel/analytics/react';
import { Logger, LogCategory, logNavigation } from '@/utils/logger';
import { ToastProvider } from '@/components/ui/Toast';
import { supabase } from '@/lib/supabase';
import { useAuthStore, useIsAuthenticated } from '@/stores/authStore';
import { ROUTES } from '@/constants';
import {
  LoginPage,
  SignupPage,
  DashboardPage,
  PhoneNumbersPage,
  PhoneDetailPage,
  PhoneNumberGroupsPage,
  SettingsPage,
  UsersSettingsPage,
  BillingSettingsPage,
  NotificationsSettingsPage,
  IntegrationsPage,
  SecuritySettingsPage,
  AdminOverviewPage,
  AdminCustomersPage,
  AdminCustomerDetailPage,
  AdminPromotionsPage,
  AdminAnalyticsPage,
  AdminAuditLogPage,
} from '@/pages';
import { SetPasswordPage } from '@/pages/SetPasswordPage';

// ============================================================================
// Route Protection Components
// ============================================================================

interface ProtectedRouteProps {
  children: React.ReactNode;
}

function ProtectedRoute({ children }: ProtectedRouteProps) {
  const isAuthenticated = useIsAuthenticated();
  const location = useLocation();

  useEffect(() => {
    Logger.debug(LogCategory.AUTH, 'ProtectedRoute check', 'ProtectedRoute', {
      isAuthenticated,
      path: location.pathname,
    });
  }, [isAuthenticated, location.pathname]);

  if (!isAuthenticated) {
    Logger.info(LogCategory.AUTH, 'Redirecting unauthenticated user to login', 'ProtectedRoute', {
      from: location.pathname,
    });
    return <Navigate to={ROUTES.LOGIN} state={{ from: location }} replace />;
  }

  return <>{children}</>;
}

interface PublicRouteProps {
  children: React.ReactNode;
}

function PublicRoute({ children }: PublicRouteProps) {
  const isAuthenticated = useIsAuthenticated();
  const location = useLocation();

  useEffect(() => {
    Logger.debug(LogCategory.AUTH, 'PublicRoute check', 'PublicRoute', {
      isAuthenticated,
      path: location.pathname,
    });
  }, [isAuthenticated, location.pathname]);

  // Allow access to public routes if not authenticated
  if (!isAuthenticated) {
    return <>{children}</>;
  }

  // If authenticated, redirect to dashboard
  const from = (location.state as { from?: { pathname: string } })?.from?.pathname;
  const redirectTo = from && from !== ROUTES.LOGIN && from !== ROUTES.SIGNUP ? from : ROUTES.DASHBOARD;

  Logger.info(LogCategory.AUTH, 'Redirecting authenticated user from public route', 'PublicRoute', {
    to: redirectTo,
  });

  return <Navigate to={redirectTo} replace />;
}

// ============================================================================
// Navigation Logger
// ============================================================================

function NavigationLogger() {
  const location = useLocation();

  useEffect(() => {
    logNavigation(
      document.referrer || 'direct',
      location.pathname,
      'user',
      { search: location.search, hash: location.hash }
    );
  }, [location]);

  return null;
}

// ============================================================================
// OAuth Callback Handler
// ============================================================================

function OAuthCallbackPage() {
  const navigate = useNavigate();
  const initializeAuth = useAuthStore((state) => state.initializeAuth);

  useEffect(() => {
    Logger.info(LogCategory.AUTH, 'Processing OAuth callback', 'OAuthCallbackPage');

    // Supabase handles the token exchange from the URL hash automatically.
    // We just need to wait for the session to be established, then load the profile.
    const handleCallback = async () => {
      try {
        // Check if this is a password recovery link
        const hash = window.location.hash;
        const isRecovery = hash.includes('type=recovery');

        // Wait for Supabase to process the hash fragment and establish a session
        const { data: { session }, error } = await supabase.auth.getSession();

        if (error) {
          Logger.error(LogCategory.AUTH, 'OAuth session error', 'OAuthCallbackPage', undefined, error);
          navigate(ROUTES.LOGIN, { state: { error: 'Authentication failed. Please try again.' } });
          return;
        }

        if (!session) {
          Logger.info(LogCategory.AUTH, 'No session yet, waiting for auth state change', 'OAuthCallbackPage');
          return;
        }

        // If this is a recovery/invite link, redirect to set password
        if (isRecovery) {
          Logger.info(LogCategory.AUTH, 'Recovery link detected, redirecting to set password', 'OAuthCallbackPage');
          await initializeAuth();
          navigate(ROUTES.SET_PASSWORD);
          return;
        }

        // Check if user profile exists, if not create it (first Google login)
        const { data: profile, error: profileError } = await (supabase.rpc as any)('get_my_profile');

        if (profileError) {
          Logger.info(LogCategory.AUTH, 'No profile found, creating for OAuth user', 'OAuthCallbackPage');

          const { error: signupError } = await (supabase.rpc as any)('handle_signup', {
            p_user_id: session.user.id,
            p_email: session.user.email,
            p_name: session.user.user_metadata?.full_name || session.user.user_metadata?.name || session.user.email?.split('@')[0] || 'User',
            p_company_name: `${session.user.email?.split('@')[0]}'s Company`,
            p_domain: `google-${session.user.id.substring(0, 8)}`,
            p_phone_number: null,
            p_industry: null,
            p_organization_size: null,
          });

          if (signupError) {
            Logger.error(LogCategory.AUTH, 'Failed to create OAuth profile', 'OAuthCallbackPage', undefined, signupError);
            navigate(ROUTES.LOGIN, { state: { error: 'Failed to set up your account. Please try again.' } });
            return;
          }
        }

        await initializeAuth();
        Logger.info(LogCategory.AUTH, 'OAuth callback successful', 'OAuthCallbackPage');
        navigate(ROUTES.DASHBOARD);
      } catch (err) {
        Logger.error(LogCategory.AUTH, 'OAuth callback failed', 'OAuthCallbackPage', undefined, err);
        navigate(ROUTES.LOGIN, { state: { error: 'Authentication failed. Please try again.' } });
      }
    };

    handleCallback();
  }, [navigate, initializeAuth]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
        <p className="mt-4 text-slate-600">Completing authentication...</p>
      </div>
    </div>
  );
}

// ============================================================================
// 404 Page
// ============================================================================

function NotFoundPage() {
  const navigate = useNavigate();

  useEffect(() => {
    Logger.warn(LogCategory.NAVIGATION, '404 - Page not found', 'NotFoundPage', {
      path: window.location.pathname,
    });
  }, []);

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50">
      <div className="text-center">
        <h1 className="text-6xl font-bold text-slate-300">404</h1>
        <p className="mt-4 text-xl text-slate-600">Page not found</p>
        <p className="mt-2 text-slate-500">The page you're looking for doesn't exist.</p>
        <button
          onClick={() => navigate(ROUTES.DASHBOARD)}
          className="mt-6 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
        >
          Go to Dashboard
        </button>
      </div>
    </div>
  );
}

// ============================================================================
// App Routes
// ============================================================================

function AppRoutes() {
  return (
    <>
      <NavigationLogger />
      <Routes>
        {/* Public Routes */}
        <Route
          path={ROUTES.LOGIN}
          element={
            <PublicRoute>
              <LoginPage />
            </PublicRoute>
          }
        />
        <Route
          path={ROUTES.SIGNUP}
          element={
            <PublicRoute>
              <SignupPage />
            </PublicRoute>
          }
        />
        <Route path={ROUTES.AUTH_CALLBACK} element={<OAuthCallbackPage />} />
        <Route path={ROUTES.SET_PASSWORD} element={<SetPasswordPage />} />

        {/* Protected Routes */}
        <Route
          path={ROUTES.DASHBOARD}
          element={
            <ProtectedRoute>
              <DashboardPage />
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTES.PHONE_NUMBERS}
          element={
            <ProtectedRoute>
              <PhoneNumbersPage />
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTES.PHONE_DETAIL}
          element={
            <ProtectedRoute>
              <PhoneDetailPage />
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTES.PHONE_NUMBER_GROUPS}
          element={
            <ProtectedRoute>
              <PhoneNumberGroupsPage />
            </ProtectedRoute>
          }
        />

        {/* Settings Routes */}
        <Route
          path={ROUTES.SETTINGS}
          element={
            <ProtectedRoute>
              <SettingsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTES.SETTINGS_USERS}
          element={
            <ProtectedRoute>
              <UsersSettingsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTES.SETTINGS_BILLING}
          element={
            <ProtectedRoute>
              <BillingSettingsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTES.SETTINGS_NOTIFICATIONS}
          element={
            <ProtectedRoute>
              <NotificationsSettingsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTES.SETTINGS_INTEGRATIONS}
          element={
            <ProtectedRoute>
              <IntegrationsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTES.SETTINGS_SECURITY}
          element={
            <ProtectedRoute>
              <SecuritySettingsPage />
            </ProtectedRoute>
          }
        />

        {/* Admin Routes */}
        <Route
          path={ROUTES.ADMIN}
          element={
            <ProtectedRoute>
              <AdminOverviewPage />
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTES.ADMIN_CUSTOMERS}
          element={
            <ProtectedRoute>
              <AdminCustomersPage />
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTES.ADMIN_CUSTOMER_DETAIL}
          element={
            <ProtectedRoute>
              <AdminCustomerDetailPage />
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTES.ADMIN_PROMOTIONS}
          element={
            <ProtectedRoute>
              <AdminPromotionsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTES.ADMIN_ANALYTICS}
          element={
            <ProtectedRoute>
              <AdminAnalyticsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTES.ADMIN_AUDIT_LOG}
          element={
            <ProtectedRoute>
              <AdminAuditLogPage />
            </ProtectedRoute>
          }
        />

        {/* Redirects */}
        <Route path="/" element={<Navigate to={ROUTES.DASHBOARD} replace />} />

        {/* 404 */}
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </>
  );
}

// ============================================================================
// Main App Component
// ============================================================================

function App() {
  const initializeAuth = useAuthStore((state) => state.initializeAuth);

  useEffect(() => {
    Logger.info(LogCategory.SYSTEM, 'Application initialized', 'App', {
      version: import.meta.env.VITE_APP_VERSION || '1.0.0',
      environment: import.meta.env.MODE,
    });

    // Initialize Supabase authentication
    initializeAuth();

    // Log performance metrics
    if (typeof window !== 'undefined' && window.performance) {
      const navigationTiming = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming;
      if (navigationTiming) {
        Logger.info(LogCategory.PERFORMANCE, 'Initial page load metrics', 'App', {
          domContentLoaded: Math.round(navigationTiming.domContentLoadedEventEnd - navigationTiming.startTime),
          loadComplete: Math.round(navigationTiming.loadEventEnd - navigationTiming.startTime),
          domInteractive: Math.round(navigationTiming.domInteractive - navigationTiming.startTime),
        });
      }
    }

    // Global error handler
    const handleGlobalError = (event: ErrorEvent) => {
      Logger.fatal(LogCategory.SYSTEM, 'Unhandled error', 'GlobalErrorHandler', {
        message: event.message,
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
      }, event.error);
    };

    // Unhandled promise rejection handler
    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      Logger.fatal(LogCategory.SYSTEM, 'Unhandled promise rejection', 'GlobalErrorHandler', {
        reason: event.reason?.message || String(event.reason),
      }, event.reason);
    };

    window.addEventListener('error', handleGlobalError);
    window.addEventListener('unhandledrejection', handleUnhandledRejection);

    return () => {
      window.removeEventListener('error', handleGlobalError);
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
    };
  }, []);

  return (
    <BrowserRouter>
      <ToastProvider>
        <AppRoutes />
      </ToastProvider>
      <Analytics />
    </BrowserRouter>
  );
}

export default App;
